import { Typography, Form, Tooltip, Input, Upload, Button, Tag } from 'antd';
import { QuestionCircleOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  convertCsvToMatrix,
  convertMatrixToCsvString,
  downloadCsvFile,
  forceCsvCellAsText,
  readFileAsString,
  stripCsvTextForcing,
} from '~/store/files/fileUtils';
import { useState } from 'react';
import type { Rule } from 'antd/lib/form';
import { Flex } from '../Flex';

const { Text } = Typography;
const MAX_POSTAL_CODE_DISPLAY_COUNT = 20;

export interface RuleSetPostalCodeFormEntryProps {
  disabled: boolean;
  postalCodes: string[];
  handlePostalCodeEntry: (pc: string[]) => void;
  handlePostalCodeClose: (postalCode: string) => void;
  handleClear: () => void;
  formFieldName: string;
  rules?: Rule[];
}
export const RuleSetPostalCodeFormEntry = ({
  handlePostalCodeEntry,
  rules,
  formFieldName,
  postalCodes,
  disabled,
  handleClear,
  handlePostalCodeClose,
}: RuleSetPostalCodeFormEntryProps) => {
  const [searchValue, setSearchValue] = useState('');

  const handlePostalCodeDownload = async () => {
    const csvMatrix = [['postalCodes'], ...postalCodes.map((code) => [forceCsvCellAsText(code)])];
    const csvString = await convertMatrixToCsvString(csvMatrix);
    downloadCsvFile(csvString, 'SHIPIUM_CARRIER_SELECTION_RULE_POSTAL_VALUES');
  };
  return (
    <Flex direction="vertical" gap="sm">
      <Text>
        Postal codes{' '}
        <Tooltip title={'Input supports alpha-numeric characters separated by a comma.'}>
          <QuestionCircleOutlined />
        </Tooltip>
      </Text>
      <Flex direction="horizontal" gap="xs">
        <Input.Search
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Separate with commas"
          allowClear
          enterButton="Add"
          onPaste={(event) => {
            try {
              const value = event.clipboardData.getData('text/plain');
              if (value && value.match('^[a-zA-Z0-9, ]+$')) {
                event.preventDefault();
                const values = value.split(',');
                handlePostalCodeEntry(values);
                setSearchValue('');
              }
            } catch {}
          }}
          onSearch={(value, e) => {
            if (value.match('^[a-zA-Z0-9, ]+$')) {
              e?.preventDefault();
              const values = value.split(',');
              handlePostalCodeEntry(values);
              setSearchValue('');
            }
          }}
          {...{ disabled }}
        />

        <Upload
          accept=".csv"
          beforeUpload={() => false}
          multiple={false}
          showUploadList={false}
          onChange={async (val) => {
            const csvString = await readFileAsString(val.file as any);
            const csvMatrix = convertCsvToMatrix(csvString);
            const values = [];
            if (csvMatrix && csvMatrix.length > 1) {
              for (let i = 1; i < csvMatrix.length; i++) {
                if (csvMatrix[i].length > 0) {
                  values.push(stripCsvTextForcing(csvMatrix[i][0])); // Assumes value is in column A
                }
              }
            }
            handlePostalCodeEntry(values);
          }}
        >
          <Button disabled={disabled}>
            <UploadOutlined /> Upload
          </Button>
        </Upload>

        <Button
          onClick={() => {
            handlePostalCodeDownload();
          }}
          disabled={disabled}
        >
          <DownloadOutlined /> Download
        </Button>

        <Button onClick={handleClear} disabled={disabled}>
          Clear All
        </Button>
      </Flex>
      <Form.Item name={formFieldName} rules={rules}>
        {postalCodes.slice(0, Math.min(postalCodes.length, MAX_POSTAL_CODE_DISPLAY_COUNT)).map((tagEntry, index) => {
          return (
            <Tag
              key={index}
              closable
              style={{ overflow: 'hidden' }}
              onClose={(e) => {
                e.preventDefault();
                handlePostalCodeClose(tagEntry);
              }}
            >
              {tagEntry}
            </Tag>
          );
        })}
        {postalCodes.length > MAX_POSTAL_CODE_DISPLAY_COUNT && (
          <Tag>({postalCodes.length - MAX_POSTAL_CODE_DISPLAY_COUNT}) Additional Values - Please Download to View</Tag>
        )}
      </Form.Item>
    </Flex>
  );
};
